import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import { contactService } from '../../services/contactService.js';
import { Plus, Mail, Filter, ArrowLeft, Pencil, Package, MoreVertical, HandCoins, XCircle, CreditCard, MessageSquareQuote } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const ViewContact = () => {
  const { contactId } = useParams();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchContact();
  }, [contactId]);

  const fetchContact = async () => {
    try {
      const response = await contactService.getContact(contactId);
      setContact(response.data.data);
    } catch (err) {
      Alert.error('Failed to load contact details');
      console.error(err);
      navigate('/contact');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-sm sm:text-base text-gray-600">Loading contact details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!contact) {
    return null;
  }

  return (
    <DashboardLayout>
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <Mail className="w-6 h-6 sm:w-7 sm:h-7 text-buttonBg" /> Contact Details
                  </h1>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  {/* Header Section */}
                  <div className="bg-primary p-4 sm:p-6 md:p-8 md:py-4">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 overflow-hidden border-4 border-white">
                        <span className="text-4xl sm:text-5xl">👤</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-tertiary mb-0">
                          {contact.full_name}
                        </h2>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <p className="text-tertiary text-base sm:text-lg">{contact.email}</p>
                          <span className={`px-2.5 sm:px-3 py-1 rounded-xl text-xs sm:text-sm font-semibold ${
                            contact.reply
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {contact.reply ? 'Replied' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    {/* Contact Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Contact Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Contact ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{contact.id}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Full Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{contact.full_name}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">First Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{contact.first_name}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Last Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{contact.last_name}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Email Address</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{contact.email}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Phone Number</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{contact.phone}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Location</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{contact.location || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Message Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Message Details
                      </h3>
                      <div className="space-y-4 sm:space-y-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Subject</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{contact.subject}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Message</label>
                          <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                            <p className="text-sm sm:text-base text-gray-900 leading-relaxed whitespace-pre-wrap break-words">
                              {contact.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Reply Information */}
                    {contact.reply && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Reply Information
                        </h3>
                        <div className="space-y-4 sm:space-y-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Reply Message</label>
                            <div className="bg-green-50 rounded-xl p-3 sm:p-4 border border-green-200">
                              <p className="text-sm sm:text-base text-gray-900 leading-relaxed whitespace-pre-wrap break-words">
                                {contact.reply}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            <div>
                              <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Replied By</label>
                              <p className="text-sm sm:text-base text-secondary font-semibold break-all">{contact.replied_by_name || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Replied At</label>
                              <p className="text-sm sm:text-base text-secondary font-semibold break-all break-words">{formatDate(contact.replied_at)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Status</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            {contact.reply ? 'Replied' : 'Pending Reply'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Date Received</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all break-words">{formatDate(contact.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all break-words">{formatDate(contact.updated_at)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Additional Information
                      </h3>
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <span className="text-xl sm:text-2xl flex-shrink-0">ℹ️</span>
                          <div>
                            <h4 className="font-semibold text-sm sm:text-base text-gray-900 mb-1">About Contact Messages</h4>
                            <p className="text-xs sm:text-sm text-gray-700">
                              Contact messages are submitted by customers through the website contact form. 
                              Each message should be reviewed and replied to promptly to maintain good customer service.
                              {!contact.reply && ' This message is still pending a reply.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="bg-gray-50 px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 flex flex-col sm:flex-row gap-3 sm:gap-4 border-t">
                    {!contact.reply && (
                      <Button
                        variant="primary"
                        icon={Pencil}
                        onClick={() => navigate(`/contact/edit/${contactId}`)}
                      >
                        Send Reply
                      </Button>
                    )}
                    {contact.reply && (
                      <Button
                        variant="primary"
                        onClick={() => navigate(`/contact/edit/${contactId}`)}
                      >
                        👁️ View Reply Details
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      icon={ArrowLeft}
                      onClick={() => navigate('/contact')}
                    >
                      Back to Contacts
                    </Button>
                  </div>
                </div>
    </DashboardLayout>
  );
};

export default ViewContact;